import { useState } from "react";
import { Link } from "react-router-dom";
import { fmtEur, fmtPct, NC, STATUS_COLORS, label } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export const Money = ({ v, signed = false, className = "" }) => {
  if (v === null || v === undefined) return <span className={`text-slate-400 italic ${className}`}>{NC}</span>;
  const cls = signed ? (v > 0 ? "pos" : v < 0 ? "neg" : "") : v < 0 ? "neg" : "";
  return <span className={`tabular-nums ${cls} ${className}`}>{fmtEur(v)}</span>;
};

export const Pct = ({ v, signed = true }) => {
  if (v === null || v === undefined) return <span className="text-slate-400 italic">{NC}</span>;
  return <span className={`tabular-nums ${signed ? (v > 0 ? "pos" : v < 0 ? "neg" : "") : ""}`}>{fmtPct(v)}</span>;
};

export const StatusBadge = ({ s, text }) => (
  <span data-testid={`badge-${s}`} className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium whitespace-nowrap ${STATUS_COLORS[s] || "badge-na"}`}>{text || label(s)}</span>
);

export function KpiCard({ title, value, sub, to, testId, tone }) {
  const inner = (
    <div className={`kpi-card h-full ${to ? "hover:border-primary hover:shadow transition-colors duration-200 cursor-pointer" : ""}`} data-testid={testId}>
      <span className="text-xs uppercase tracking-wide text-slate-500 font-medium">{title}</span>
      <span className={`text-xl font-heading font-semibold ${tone === "neg" ? "neg" : tone === "pos" ? "pos" : "text-slate-900"} truncate`}>{value}</span>
      {sub && <span className="text-xs text-slate-500">{sub}</span>}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
    <div>
      <h1 className="text-2xl md:text-3xl font-heading font-semibold text-slate-900">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export function ConfirmButton({ onConfirm, title = "Confermi l'operazione?", description, children, variant = "destructive", size = "sm", testId, requireReason = false, disabled }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  return (
    <>
      <Button variant={variant} size={size} onClick={() => setOpen(true)} data-testid={testId} disabled={disabled}>{children}</Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">{title}</AlertDialogTitle>
            {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
          </AlertDialogHeader>
          {requireReason && <textarea data-testid={`${testId}-reason`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivazione (obbligatoria)" className="input-editable" rows={3} />}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testId}-cancel`}>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid={`${testId}-confirm`} disabled={requireReason && reason.trim().length < 5} onClick={() => { onConfirm(reason); setReason(""); }}>Conferma</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export const Empty = ({ text = "Nessun dato disponibile" }) => <div className="text-sm text-slate-500 border border-dashed rounded-md p-6 text-center bg-white">{text}</div>;
export const Loading = () => <div className="text-sm text-slate-500 p-6 animate-pulse" data-testid="loading">Caricamento…</div>;
